import React from 'react';
import type { LlmEndpointLink } from '../../../../shared/llm';
import { langData } from '../../../../src/lang';
import { LangBlock, ShowNotify } from '../../global';
import { WebStorage } from '../../../storage';
import { AdminLlmModeration } from './admin.llm.moderation';
import {
    AdminButton,
    AdminCard,
    AdminEmpty,
    AdminPageWrap,
    AdminSection,
} from './admin.ui';

const LLM_I18N: Record<string, { title: langData; desc: langData }> = {
    products: { title: 'AdminLlmProductsTitle', desc: 'AdminLlmProductsDesc' },
    addons: { title: 'AdminLlmAddonsTitle', desc: 'AdminLlmAddonsDesc' },
    hotkeys: { title: 'AdminLlmHotkeysTitle', desc: 'AdminLlmHotkeysDesc' },
    streamalerts: { title: 'AdminLlmStreamalertsTitle', desc: 'AdminLlmStreamalertsDesc' },
    'streamkit-plus': { title: 'AdminLlmStreamkitTitle', desc: 'AdminLlmStreamkitDesc' },
};

const copyText = async (
    text: string,
    label: string,
    strings: { copied: string; failed: string },
) => {
    try {
        await navigator.clipboard.writeText(text);
        ShowNotify(strings.copied, label, 'success');
    } catch {
        ShowNotify(strings.failed, label, 'error');
    }
};

const EndpointUrlRow = ({ label, url }: { label: string; url: string }) => {
    const [, , LangString] = LangBlock();

    return (
        <div className="admin-llm-url">
            <span className="admin-llm-url__label">{label}</span>
            <code className="admin-llm-url__value">{url}</code>
            <div className="admin-llm-url__actions">
                <AdminButton
                    variant="primary"
                    size="sm"
                    onClick={() =>
                        copyText(url, LangString('AdminLlmUrlSuffix', label), {
                            copied: LangString('AdminCopied'),
                            failed: LangString('AdminCopyFailed'),
                        })
                    }
                >
                    {LangString('AdminCopy')}
                </AdminButton>
                <a
                    className="admin-btn admin-btn--ghost admin-btn--sm"
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                >
                    {LangString('AdminOpen')}
                </a>
            </div>
        </div>
    );
};

export const AdminLlm = () => {
    const [, , LangString] = LangBlock();
    const [endpoints, setEndpoints] = React.useState<LlmEndpointLink[]>([]);
    const [loaded, setLoaded] = React.useState(false);
    const [error, setError] = React.useState('');

    React.useEffect(() => {
        WebStorage.ApiRequest('/api/admin/llm/endpoints').then((res) => {
            if (res.status && Array.isArray(res.endpoints)) {
                setEndpoints(res.endpoints);
                setError('');
            } else {
                setEndpoints([]);
                setError(res.error_message || LangString('AdminLlmLoadFailed'));
            }

            setLoaded(true);
        });
    }, [LangString]);

    return (
        <AdminPageWrap>
            <AdminLlmModeration />
            <AdminSection
                title={LangString('AdminLlmTitle')}
                subtitle={LangString('AdminLlmSubtitle')}
            >
                {!loaded ? (
                    <AdminEmpty message={LangString('AdminLoadingEndpoints')} />
                ) : error ? (
                    <AdminEmpty message={error} />
                ) : endpoints.length === 0 ? (
                    <AdminEmpty message={LangString('AdminNoLlmEndpoints')} />
                ) : (
                    <div className="admin-llm-list">
                        {endpoints.map((item) => {
                            const i18n = LLM_I18N[item.id];

                            return (
                                <AdminCard
                                    key={item.id}
                                    title={i18n ? LangString(i18n.title) : item.title}
                                >
                                    <p className="admin-llm-card__desc">
                                        {i18n ? LangString(i18n.desc) : item.description}
                                    </p>
                                    <EndpointUrlRow
                                        label={LangString('AdminLlmJson')}
                                        url={item.jsonUrl}
                                    />
                                    <EndpointUrlRow
                                        label={LangString('AdminLlmMarkdown')}
                                        url={item.markdownUrl}
                                    />
                                </AdminCard>
                            );
                        })}
                    </div>
                )}
            </AdminSection>
        </AdminPageWrap>
    );
};
